import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Leaf, Timer, ShieldCheck, ChefHat, Package } from "lucide-react";
import { FadeUp, MaskedLine, ParallaxImage } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import Seo from "@/components/Seo";

const PASTA_IMG =
  "https://images.unsplash.com/photo-1608897013039-887f21d8c804?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxnbHV0ZW4lMjBmcmVlJTIwcGFzdGF8ZW58MHx8fHwxNzg2MjAxMjYzfDA&ixlib=rb-4.1.0&q=85";
const INGREDIENTS_IMG =
  "https://images.unsplash.com/photo-1576398289164-c48dc021b4e1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHw0fHxnbHV0ZW4lMjBmcmVlJTIwbWVhbCUyMGZyZXNoJTIwaW5ncmVkaWVudHN8ZW58MHx8fHwxNzg2MjAxMjU1fDA&ixlib=rb-4.1.0&q=85";
const CHEF_IMG =
  "https://images.pexels.com/photos/4253133/pexels-photo-4253133.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const manifesto = [
  {
    n: "01",
    title: "Gusto, prima di tutto",
    text: "Senza glutine non deve significare senza sapore. Le ricette Propaq nascono per convincere anche chi il glutine può mangiarlo.",
    icon: ChefHat,
  },
  {
    n: "02",
    title: "Cinque minuti",
    text: "Dal frigo alla tavola in cinque minuti: la comodità del piatto pronto con la cura della cucina artigianale.",
    icon: Timer,
  },
  {
    n: "03",
    title: "Sicurezza certificata",
    text: "Laboratori dedicati e controlli rigorosi: ogni piatto è sicuro per celiaci e intolleranti, senza compromessi.",
    icon: ShieldCheck,
  },
  {
    n: "04",
    title: "Qualità Michelchef",
    text: "Propaq è un marchio di Michelchef srl: produzione propria, ingredienti selezionati, filiera corta e controllata.",
    icon: Leaf,
  },
  {
    n: "05",
    title: "Formati per ogni canale",
    text: "Piatti monoporzione e sughi in placca da 1 kg o monoporzionati da 200 g: soluzioni per retail, gastronomie e ristorazione.",
    icon: Package,
  },
];

const Propaq = () => (
  <div data-testid="propaq-page">
    <Seo
      title="Propaq — Piatti Pronti Gluten Free in 5 Minuti | COMMPAQ"
      description="Piatti pronti e sughi 100% gluten free di Michelchef srl, commercializzati da COMMPAQ: pronti in 5 minuti, shelf-life 365 giorni, per retail e ristorazione."
      image="/catalog/piatto-pronti.png"
    />
    <section className="relative flex min-h-[92vh] items-center overflow-hidden pt-28 pb-20">
      <p
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 select-none whitespace-nowrap font-display text-[22vw] font-semibold text-outline"
      >
        PROPAQ
      </p>
      <div className="relative mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-16 px-6 lg:grid-cols-12 lg:px-10">
        <div className="lg:col-span-6">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="text-sm uppercase tracking-[0.3em] text-moss"
          >
            Il marchio · by Michelchef srl
          </motion.p>
          <h1
            data-testid="propaq-heading"
            className="mt-8 font-display text-5xl font-medium leading-[0.98] tracking-tight text-forest sm:text-6xl lg:text-7xl"
          >
            <MaskedLine delay={0.15}>Piatti pronti</MaskedLine>
            <MaskedLine delay={0.3} className="italic text-moss">
              gluten free.
            </MaskedLine>
            <MaskedLine delay={0.45}>Davvero buoni.</MaskedLine>
          </h1>
          <FadeUp delay={0.7} className="mt-10 max-w-md">
            <p className="text-base leading-relaxed text-forest/70">
              Propaq è la linea di piatti pronti e sughi gluten free prodotta da Michelchef srl e
              commercializzata da COMMPAQ: pronti in cinque minuti, consegnati in tutta Italia.
            </p>
            <Link
              to="/contatti"
              data-testid="propaq-cta"
              className="group mt-10 inline-flex items-center gap-3 rounded-full bg-moss px-8 py-4 text-sm uppercase tracking-[0.15em] text-bone transition-[transform,box-shadow,background-color] duration-300 hover:-translate-y-1 hover:bg-forest hover:shadow-xl hover:shadow-moss/30"
            >
              Richiedi il listino
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </FadeUp>
        </div>
        <div className="lg:col-span-6">
          <motion.div
            animate={{ y: [0, -14, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            className="relative mx-auto max-w-md"
          >
            <motion.div
              initial={{ clipPath: "inset(0 0 100% 0)" }}
              animate={{ clipPath: "inset(0 0 0% 0)" }}
              transition={{ duration: 1.3, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            >
              <img
                data-testid="propaq-hero-image"
                src={PASTA_IMG}
                alt="Piatto di pasta gluten free Propaq"
                className="aspect-square w-full rounded-full object-cover shadow-2xl shadow-forest/20"
              />
            </motion.div>
            <motion.div
              data-testid="propaq-hero-badge"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 1.2 }}
              className="absolute -right-4 top-8 rounded-full bg-ochre px-6 py-3 text-sm uppercase tracking-[0.15em] text-forest shadow-xl"
            >
              100% Gluten Free
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>

    <Marquee
      items={["Gusto autentico", "Pronto in 5 minuti", "Laboratorio dedicato", "Michelchef srl"]}
      className="border-y border-forest/10"
    />

    <section className="mx-auto max-w-7xl px-6 py-28 lg:px-10 lg:py-40">
      <FadeUp>
        <p className="text-sm uppercase tracking-[0.3em] text-moss">Manifesto</p>
        <h2 className="mt-6 max-w-2xl font-display text-4xl font-medium leading-none text-forest sm:text-5xl">
          Cinque capitoli, <span className="italic text-ochre">una promessa.</span>
        </h2>
      </FadeUp>
      <div className="mt-20">
        {manifesto.map((m, i) => (
          <FadeUp key={m.n} delay={0.05 * i}>
            <div
              data-testid={`manifesto-chapter-${m.n}`}
              className="group grid grid-cols-1 items-start gap-6 border-t border-forest/10 py-12 transition-colors duration-500 hover:bg-sage/10 md:grid-cols-12 md:items-center md:px-6"
            >
              <span className="font-display text-6xl italic text-outline transition-colors duration-500 md:col-span-2">
                {m.n}
              </span>
              <h3 className="font-display text-3xl font-medium text-forest md:col-span-4">{m.title}</h3>
              <p className="leading-relaxed text-forest/70 md:col-span-5">{m.text}</p>
              <m.icon size={30} className="text-sage transition-colors duration-500 group-hover:text-moss md:col-span-1" />
            </div>
          </FadeUp>
        ))}
      </div>
    </section>

    <section className="bg-forest py-28 text-bone lg:py-36">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <FadeUp>
          <p className="text-sm uppercase tracking-[0.3em] text-sage">La gamma</p>
          <h2 className="mt-6 font-display text-4xl font-medium leading-none sm:text-5xl">
            Dalla dispensa <span className="italic text-ochre">alla tavola.</span>
          </h2>
        </FadeUp>
        <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-3">
          {[
            {
              img: "/catalog/piatto-pronti.png",
              title: "Piatti Pronti",
              text: "Primi e piatti unici gluten free, pronti in cinque minuti. Basta scaldare e servire.",
              testId: "range-piatti",
            },
            {
              img: "/catalog/piatto-sughi.png",
              title: "Sughi Pronti",
              text: "Sughi, salse e brodi in placche da 1 kg o monoporzioni da 200 g, per casa e professionisti.",
              testId: "range-sughi",
            },
            {
              img: "/chef-michelchef.png",
              title: "Qualità Michelchef",
              text: "Produzione propria nei laboratori Michelchef srl, con ingredienti selezionati e filiera controllata.",
              testId: "range-michelchef",
            },
          ].map((c, i) => (
            <FadeUp key={c.title} delay={i * 0.12}>
              <div
                data-testid={c.testId}
                className="group h-full border border-bone/10 bg-forest transition-[background-color,transform] duration-500 hover:-translate-y-2"
              >
                <div className="overflow-hidden">
                  <img
                    src={c.img}
                    alt={c.title}
                    className="aspect-[4/3] w-full object-contain p-4 drop-shadow-2xl transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="p-8">
                  <h3 className="font-display text-2xl font-medium">{c.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-bone/65">{c.text}</p>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
        <FadeUp delay={0.2}>
          <div className="mt-20 flex flex-col items-start justify-between gap-8 border-t border-bone/10 pt-14 md:flex-row md:items-center">
            <p className="max-w-xl font-display text-2xl italic leading-snug text-bone/85">
              "Sei un rivenditore, una gastronomia o un ristoratore? Porta Propaq nel tuo assortimento."
            </p>
            <Link
              to="/contatti"
              data-testid="propaq-contact-cta"
              className="inline-flex items-center gap-3 rounded-full bg-ochre px-10 py-4 text-sm uppercase tracking-[0.15em] text-forest transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-ochre/20"
            >
              Diventa rivenditore
              <ArrowRight size={16} />
            </Link>
          </div>
        </FadeUp>
      </div>
    </section>
  </div>
);

export default Propaq;
